import {IonItem, IonLabel} from "@ionic/react";
import {type Ref, useMemo, useRef} from "react";
import {isNil} from "app/utils/stdlib";
import {hofForwardRef, type MaybeNil} from "app/utils/types";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {RequiredLabel} from "app/employee/incdoc/RequiredLabel";
import {ErrorMessage} from "app/employee/incdoc/supplier/ErrorMessage";
import {IdComboBox, type IdComboBoxProps} from "app/employee/controlCard/IdComboBox";

export interface TypeaheadMultiSelectProps<Option, Value> extends IdComboBoxProps<Option, Value> {
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  placeholder?: string;
}

export const TypeaheadMultiSelect = hofForwardRef(
  <Option, Value>(props: TypeaheadMultiSelectProps<Option, Value>, ref: Ref<HTMLIonItemElement>) => {
    const {title, required, disabled, errorMessage, placeholder, onSelect, value, options, isEqual, textExtractor} =
      props;
    const modal = useRef<HTMLIonModalElement>(null);

    const handleSelect = async (option: MaybeNil<Option[]>) => {
      await onSelect?.(option);
      await modal.current?.dismiss();
    };

    const selectedTexts = useMemo(() => {
      if (isNil(options) || isNil(value) || value.length === 0) return null;
      const texts: string[] = [];
      for (const option of options) {
        if (value.some((v) => isEqual(option, v))) {
          texts.push(textExtractor(option));
        }
      }
      return texts.length > 0 ? texts.join(", ") : null;
    }, [isEqual, options, textExtractor, value]);

    const label = (
      <>
        {title} {required && isNil(errorMessage) ? <RequiredLabel value={selectedTexts} /> : null}
      </>
    );

    return (
      <>
        <IonItem button detail disabled={disabled} onClick={() => modal.current?.present()} ref={ref}>
          <IonLabel className="ion-text-wrap">
            {isNil(selectedTexts) ? (
              <>
                {label}
                {placeholder ? <p>{placeholder}</p> : null}
              </>
            ) : (
              <>
                <p>{label}</p> {selectedTexts}
              </>
            )}
            {errorMessage ? (
              <p>
                <ErrorMessage>{errorMessage}</ErrorMessage>
              </p>
            ) : null}
          </IonLabel>
        </IonItem>
        <FstIonModal ref={modal}>
          <IdComboBox {...props} multiselect onSelect={handleSelect} toggleMode />
        </FstIonModal>
      </>
    );
  }
);
